import React from 'react';
import UnifiedGallery from '../components/UnifiedGallery';
import useStandardGallery from '../hooks/useStandardGallery';

/**
 * RawrockPage - COMPREHENSIVE GALLERY CONFIGURATION EXAMPLE
 * 
 * This page demonstrates ALL possible parameters for the useStandardGallery hook.
 * Use this as a reference when configuring other gallery pages.
 * 
 * AVAILABLE PARAMETERS:
 * 
 * 1. FEATURES (features object):
 *    - slideshow: boolean - Enable slideshow functionality
 *    - thumbnails: boolean - Show thumbnail navigation
 *    - fullscreen: boolean - Enable fullscreen mode
 *    - zoom: boolean - Enable zoom functionality
 *    - captions: boolean - Show photo captions
 *    - sharing: boolean - Enable sharing buttons
 *    - lazyLoading: boolean - Enable lazy loading for performance
 *    - imageOptimization: boolean - Enable image optimization
 *    - preloadAdjacent: number - Number of adjacent images to preload
 * 
 * 2. LAYOUT (layout object):
 *    - thumbnailSize: number - Size of thumbnails in pixels
 *    - maxDisplay: number|null - Maximum photos to display (null = all)
 *    - columns: 'auto'|number - Number of columns ('auto' or 1, 2, 3, 4, etc.)
 *    - spacing: 'compact'|'default'|'wide' - Spacing between photos
 * 
 * 3. PERFORMANCE (performance object):
 *    - enableIntersectionObserver: boolean - Use Intersection Observer
 *    - rootMargin: number - Root margin for Intersection Observer (pixels)
 *    - enableWebP: boolean - Enable WebP format support
 *    - enableProgressiveLoading: boolean - Enable progressive loading
 * 
 * HOOK RETURNS:
 *    - getGalleryProps: function - Returns props for UnifiedGallery
 *    - loading: boolean - Loading state
 *    - error: string|null - Error message if any
 *    - photos: array - Array of photo objects
 *    - refreshPhotos: function - Function to refresh gallery
 *    - galleryConfig: object - Current configuration
 *    - galleryRef: ref - Reference to gallery component
 */

const April122025Page = () => {
  // Use standardized gallery hook with ALL POSSIBLE PARAMETERS shown with default values
  // This page serves as a comprehensive example of all available configuration options
  const { getGalleryProps, loading, error, photos, refreshPhotos } = useStandardGallery('marco_luz', {
    // FEATURES CONFIGURATION
    features: {
      slideshow: true,              // Enable slideshow functionality
      thumbnails: true,             // Show thumbnail navigation
      fullscreen: true,             // Enable fullscreen mode
      zoom: true,                   // Enable zoom functionality
      captions: true,               // Show photo captions
      sharing: false,               // Enable sharing buttons (default: false)
      
      // PERFORMANCE FEATURES
      lazyLoading: true,            // Enable lazy loading for better performance
      imageOptimization: true,      // Enable image optimization
      preloadAdjacent: 2            // Number of adjacent images to preload (2 on each side)
    },
    
    // LAYOUT CONFIGURATION
    layout: {
      thumbnailSize: 220,           // Size of thumbnails in pixels
      maxDisplay: 10,             // Maximum number of photos to display (null = all)
      columns: 'auto',              // Number of columns: 'auto', 1, 2, 3, 4, etc.
      spacing: 'default'            // Spacing between photos: 'compact', 'default', 'wide'
    },
    
    // PERFORMANCE CONFIGURATION
    performance: {
      enableIntersectionObserver: true,    // Use Intersection Observer for performance
      rootMargin: 50,                      // Root margin for Intersection Observer (pixels)
      enableWebP: true,                    // Enable WebP format support
      enableProgressiveLoading: true       // Enable progressive image loading
    }
  });

  return (
      <div className="page-container-wrapper max-width-default padding-default">
        <div className="page-header">
          <h1>
            <i className="fas fa-calendar-day"></i>
            April 12, 2025
          </h1>
          <p>A special day worth remembering</p>
        </div>
        
        <div className="page-content">
          <div className="section">
            <div className="section-header">
              <div className="section-icon">
                <i className="fas fa-star"></i>
              </div>
              <h2>Special Celebration</h2>
            </div>
            <p>
              Welcome to April 12, 2025! This date holds special significance and memories worth preserving. 
              In mid-April, spring is in full bloom - a perfect time for celebrations, new beginnings, and creating lasting memories.
            </p>
            
            <div className="cta-buttons">
              <button className="cta-btn primary">
                <i className="fas fa-calendar-check"></i>
                Mark the Date
              </button>
              <button className="cta-btn secondary">
                <i className="fas fa-heart"></i>
                Share Memories
              </button>
            </div>

            {/* April 12 Gallery */}
            <div className="futbol-gallery-section">
              <UnifiedGallery 
                {...getGalleryProps("April 12, 2025")}
              />
            </div>
          </div>
        </div>
      </div>
  );
};

export default April122025Page;